import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Paper, Button, Chip, Avatar,
  CircularProgress, Alert, Pagination, TextField, InputAdornment,
  List, ListItem, ListItemText, ListItemAvatar,
  ListItemButton, IconButton, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, Checkbox, FormControlLabel, Collapse, Tabs, Tab, useTheme
} from '@mui/material';
import {
  Search, Notifications as NotificationsIcon, NotificationsActive,
  NotificationsOff, Delete, DeleteSweep, MarkEmailRead, MarkEmailUnread,
  Star, Person, Info, CheckCircle, Error, BookOnline, CalendarMonth,
  Tune, AllInbox, MarkunreadMailbox, DoneAll, Forum, Payment
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import notificationService from '../services/notificationService';

const getNotificationUrl = (notification) => {
  let data = notification.data || {};
  if (typeof data === 'string') {
    try { data = JSON.parse(data); } catch (e) { data = {}; }
  }
  switch (notification.type) {
    case 'NEW_BOOKING':
    case 'BOOKING_CONFIRMED':
    case 'BOOKING_CANCELLED':
    case 'BOOKING_REJECTED':
    case 'BOOKING_REMINDER':
    case 'BOOKING_CANCELLED_BY_CLIENT':
    case 'OFFER_RECEIVED':
    case 'OFFER_ACCEPTED':
    case 'OFFER_REJECTED':
    case 'NEW_OFFER_REQUEST':
    case 'NEW_OFFER_REQUEST_MESSAGE':
      return data.bookingId ? `/foglalasok?bookingId=${data.bookingId}` : null;
    case 'PAYMENT_INITIATED':
    case 'PAYMENT_CONFIRMED_BY_CLIENT':
    case 'PAYMENT_CONFIRMED_BY_PROVIDER':
    case 'PAYMENT_DEADLINE':
      return data.bookingId ? `/foglalasok?bookingId=${data.bookingId}` : '/dashboard';
    case 'NEW_REVIEW':
    case 'REVIEW_APPROVED':
    case 'REVIEW_REJECTED':
      return data.providerId ? `/szolgaltato/${data.providerId}` : null;
    case 'PROVIDER_APPROVED':
    case 'PROVIDER_REJECTED':
      return '/dashboard';
    case 'FORUM_REPLY':
      if (data.threadId && data.replyId) return `/forum/tema/${data.threadId}#reply-${data.replyId}`;
      if (data.threadId) return `/forum/tema/${data.threadId}`;
      return null;
    default:
      return null;
  }
};

const typeGroups = [
  {
    value: 'BOOKING',
    label: 'Foglalások',
    Icon: BookOnline,
    color: 'info',
    types: [
      'NEW_BOOKING', 'BOOKING_CONFIRMED', 'BOOKING_CANCELLED',
      'BOOKING_REJECTED', 'BOOKING_REMINDER', 'BOOKING_CANCELLED_BY_CLIENT',
      'OFFER_RECEIVED', 'OFFER_ACCEPTED', 'OFFER_REJECTED',
      'NEW_OFFER_REQUEST', 'NEW_OFFER_REQUEST_MESSAGE',
    ],
  },
  {
    value: 'REVIEW',
    label: 'Értékelések',
    Icon: Star,
    color: 'warning',
    types: ['NEW_REVIEW', 'REVIEW_APPROVED', 'REVIEW_REJECTED'],
  },
  {
    value: 'PROVIDER',
    label: 'Fiókkezelés',
    Icon: Person,
    color: 'success',
    types: ['PROVIDER_APPROVED', 'PROVIDER_REJECTED'],
  },
  {
    value: 'PAYMENT',
    label: 'Fizetések',
    Icon: Payment,
    color: 'success',
    types: ['PAYMENT_INITIATED', 'PAYMENT_CONFIRMED_BY_CLIENT', 'PAYMENT_CONFIRMED_BY_PROVIDER', 'PAYMENT_DEADLINE'],
  },
  {
    value: 'FORUM',
    label: 'Fórum',
    Icon: Forum,
    color: 'primary',
    types: ['FORUM_REPLY'],
  },
  {
    value: 'SYSTEM',
    label: 'Rendszer',
    Icon: Info,
    color: 'default',
    types: ['SYSTEM_UPDATE', 'PROMOTIONAL'],
  },
];

const notificationTypeLabels = {
  BOOKING_CONFIRMED: 'Foglalás megerősítve',
  BOOKING_CANCELLED: 'Foglalás lemondva',
  BOOKING_REJECTED: 'Foglalás elutasítva',
  BOOKING_CANCELLED_BY_CLIENT: 'Foglalás lemondva (ügyfél)',
  BOOKING_REMINDER: 'Foglalás emlékeztető',
  NEW_BOOKING: 'Új foglalási kérés',
  OFFER_RECEIVED: 'Ajánlat érkezett',
  OFFER_ACCEPTED: 'Ajánlat elfogadva',
  OFFER_REJECTED: 'Ajánlat elutasítva',
  NEW_REVIEW: 'Új értékelés',
  REVIEW_APPROVED: 'Értékelés jóváhagyva',
  REVIEW_REJECTED: 'Értékelés elutasítva',
  PROVIDER_APPROVED: 'Szolgáltató jóváhagyva',
  PROVIDER_REJECTED: 'Szolgáltató elutasítva',
  SYSTEM_UPDATE: 'Rendszer frissítés',
  PROMOTIONAL: 'Promóciós',
  NEW_OFFER_REQUEST: 'Új ajánlatkérés',
  NEW_OFFER_REQUEST_MESSAGE: 'Új ajánlatkérés (ismételt)',
  PAYMENT_INITIATED: 'Fizetés elindítva',
  PAYMENT_CONFIRMED_BY_CLIENT: 'Fizetés igazolva (ügyfél)',
  PAYMENT_CONFIRMED_BY_PROVIDER: 'Fizetés igazolva (szolgáltató)',
  PAYMENT_DEADLINE: 'Fizetési határidő',
  FORUM_REPLY: 'Új fórum válasz',
};

const ITEMS_PER_PAGE = 15;

const NotificationsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [allNotifications, setAllNotifications] = useState([]); // teljes, szűretlen lista (csoportszűréshez)
  const [notifications, setNotifications] = useState([]);       // megjelenített (szűrt + lapozott)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  // Szűrők
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0: Összes, 1: Olvasatlan, 2: Olvasott

  // Mobil szűrő panel
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Tömeges műveletek
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('');

  const applyGroupFilter = useCallback((list, group) => {
    if (!group) return list;
    const g = typeGroups.find(tg => tg.value === group);
    return g ? list.filter(n => g.types.includes(n.type)) : list;
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: 1,
        limit: selectedGroup ? 500 : ITEMS_PER_PAGE,
        ...(appliedSearch && { search: appliedSearch }),
      };
      if (tabValue === 1) params.isRead = false;
      if (tabValue === 2) params.isRead = true;

      const response = await notificationService.getUserNotifications(params.page, params.limit, params);

      if (response.notifications) {
        const filtered = applyGroupFilter(response.notifications, selectedGroup);

        if (selectedGroup) {
          // Kliens oldali lapozás
          const total = filtered.length;
          const start = (page - 1) * ITEMS_PER_PAGE;
          setAllNotifications(filtered);
          setNotifications(filtered.slice(start, start + ITEMS_PER_PAGE));
          setPagination({
            total,
            pages: Math.ceil(total / ITEMS_PER_PAGE),
            page,
            limit: ITEMS_PER_PAGE,
          });
        } else {
          setAllNotifications(response.notifications);
          setNotifications(filtered);
          setPagination(response.pagination || {});
        }
      }
    } catch (err) {
      console.error('Értesítések betöltése hiba:', err);
      setError('Értesítések betöltése sikertelen');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, appliedSearch, selectedGroup, tabValue, page, applyGroupFilter]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    loadNotifications();
  }, [isAuthenticated, loadNotifications, navigate]);

  // Ha selectedGroup változik, de a page nem → csoporton belül lapoz
  useEffect(() => {
    if (selectedGroup && allNotifications.length > 0) {
      const filtered = applyGroupFilter(allNotifications, selectedGroup);
      const start = (page - 1) * ITEMS_PER_PAGE;
      setNotifications(filtered.slice(start, start + ITEMS_PER_PAGE));
      setPagination(prev => ({
        ...prev,
        total: filtered.length,
        pages: Math.ceil(filtered.length / ITEMS_PER_PAGE),
        page,
      }));
    }
  }, [page, selectedGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    setAppliedSearch(searchTerm);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setAppliedSearch('');
    setSelectedGroup('');
    setPage(1);
    setSelectedNotifications(new Set());
  };

  const handleGroupSelect = (group) => {
    setSelectedGroup(prev => prev === group ? '' : group);
    setPage(1);
    setSelectedNotifications(new Set());
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(1);
    setSelectedGroup('');
    setSelectedNotifications(new Set());
  };

  const handleNotificationClick = async (notification) => {
    const url = getNotificationUrl(notification);
    if (!url) return;
    if (!notification.isRead) await handleMarkAsRead(notification.id);
    const state = {};
    if (['NEW_REVIEW', 'REVIEW_APPROVED', 'REVIEW_REJECTED'].includes(notification.type)) {
      state.scrollToReviews = true;
    }
    let data = notification.data || {};
    if (typeof data === 'string') { try { data = JSON.parse(data); } catch (e) { data = {}; } }
    if ([
      'PAYMENT_INITIATED', 'PAYMENT_CONFIRMED_BY_CLIENT', 'PAYMENT_CONFIRMED_BY_PROVIDER', 'PAYMENT_DEADLINE',
    ].includes(notification.type) && data.bookingId) {
      state.scrollToBooking = data.bookingId;
    }
    navigate(url, { state });
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n));
    } catch (err) {
      setError('Művelet sikertelen');
    }
  };

  const handleMarkAsUnread = async (notificationId) => {
    try {
      await notificationService.markAsUnread(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, isRead: false } : n));
    } catch (err) {
      setError('Művelet sikertelen');
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setSelectedNotifications(prev => { const s = new Set(prev); s.delete(notificationId); return s; });
    } catch (err) {
      setError('Törlés sikertelen');
    }
  };

  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications(prev => {
      const s = new Set(prev);
      s.has(notificationId) ? s.delete(notificationId) : s.add(notificationId);
      return s;
    });
  };

  const handleSelectAll = () => {
    setSelectedNotifications(
      selectedNotifications.size === notifications.length
        ? new Set()
        : new Set(notifications.map(n => n.id))
    );
  };

  const handleBulkAction = async () => {
    try {
      const ids = Array.from(selectedNotifications);
      if (bulkAction === 'markRead') {
        await notificationService.markMultipleAsRead(ids);
        setNotifications(prev => prev.map(n => selectedNotifications.has(n.id) ? { ...n, isRead: true } : n));
      } else if (bulkAction === 'markUnread') {
        await notificationService.markMultipleAsUnread(ids);
        setNotifications(prev => prev.map(n => selectedNotifications.has(n.id) ? { ...n, isRead: false } : n));
      } else if (bulkAction === 'delete') {
        await notificationService.deleteMultiple(ids);
        setNotifications(prev => prev.filter(n => !selectedNotifications.has(n.id)));
      }
      setSelectedNotifications(new Set());
      setBulkActionDialogOpen(false);
    } catch (err) {
      setError('Tömeges művelet sikertelen');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'BOOKING_CONFIRMED': case 'REVIEW_APPROVED': case 'PROVIDER_APPROVED': return <CheckCircle />;
      case 'BOOKING_CANCELLED': case 'BOOKING_REJECTED': case 'BOOKING_CANCELLED_BY_CLIENT': case 'REVIEW_REJECTED': case 'PROVIDER_REJECTED': return <Error />;
      case 'BOOKING_REMINDER': return <CalendarMonth />;
      case 'NEW_BOOKING': case 'NEW_OFFER_REQUEST': case 'NEW_OFFER_REQUEST_MESSAGE': return <BookOnline />;
      case 'OFFER_RECEIVED': return <NotificationsActive />;
      case 'OFFER_ACCEPTED': return <CheckCircle />;
      case 'OFFER_REJECTED': return <Error />;
      case 'NEW_REVIEW': return <Star />;
      case 'PAYMENT_INITIATED': return <Payment />;
      case 'PAYMENT_CONFIRMED_BY_CLIENT': return <Payment />;
      case 'PAYMENT_CONFIRMED_BY_PROVIDER': return <Payment />;
      case 'PAYMENT_DEADLINE': return <CalendarMonth />;
      case 'FORUM_REPLY': return <Forum />;
      case 'SYSTEM_UPDATE': return <Info />;
      case 'PROMOTIONAL': return <NotificationsActive />;
      default: return <NotificationsIcon />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'BOOKING_CONFIRMED': case 'PROVIDER_APPROVED': case 'REVIEW_APPROVED': case 'OFFER_ACCEPTED': return 'success';
      case 'BOOKING_CANCELLED': case 'BOOKING_REJECTED': case 'BOOKING_CANCELLED_BY_CLIENT': case 'PROVIDER_REJECTED': case 'REVIEW_REJECTED': case 'OFFER_REJECTED': return 'error';
      case 'BOOKING_REMINDER': return 'warning';
      case 'NEW_REVIEW': case 'NEW_BOOKING': case 'OFFER_RECEIVED': case 'NEW_OFFER_REQUEST': case 'NEW_OFFER_REQUEST_MESSAGE': return 'info';
      case 'PAYMENT_INITIATED': return 'info';
      case 'PAYMENT_CONFIRMED_BY_CLIENT': case 'PAYMENT_CONFIRMED_BY_PROVIDER': return 'success';
      case 'PAYMENT_DEADLINE': return 'warning';
      case 'FORUM_REPLY': return 'primary';
      case 'SYSTEM_UPDATE': return 'info';
      case 'PROMOTIONAL': return 'secondary';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const diffH = (Date.now() - date) / 3600000;
    if (diffH < 1) return 'Most';
    if (diffH < 24) return `${Math.floor(diffH)} órája`;
    if (diffH < 48) return 'Tegnap';
    return date.toLocaleDateString('hu-HU');
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const hasActiveFilter = appliedSearch || selectedGroup;

  if (!isAuthenticated) return null;

  if (loading && notifications.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Értesítések betöltése...</Typography>
      </Container>
    );
  }

  /* ── Sidebar tartalom ── */
  const sidebarContent = (
    <>
      {/* Cím */}
      <Box sx={{ px: 1, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
            Értesítések
          </Typography>
          {unreadCount > 0 && (
            <Chip label={unreadCount} color="error" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {pagination.total || 0} értesítés
        </Typography>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* Tab navigáció */}
      <List dense disablePadding sx={{ mb: 1 }}>
        {[
          { value: 0, label: 'Összes', Icon: AllInbox },
          { value: 1, label: 'Olvasatlan', Icon: MarkunreadMailbox, badge: tabValue !== 1 && unreadCount > 0 ? unreadCount : null },
          { value: 2, label: 'Olvasott', Icon: DoneAll },
        ].map(({ value, label, Icon, badge }) => (
          <ListItemButton
            key={value}
            selected={tabValue === value}
            onClick={() => handleTabChange(null, value)}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: 'primary.main', color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
              },
            }}
          >
            <Icon sx={{ mr: 1.5, fontSize: 20 }} />
            <ListItemText primary={label} />
            {badge !== null && badge !== undefined && (
              <Chip
                label={badge}
                color={tabValue === value ? 'default' : 'error'}
                size="small"
                sx={{
                  height: 18, fontSize: '0.65rem',
                  bgcolor: tabValue === value ? 'rgba(255,255,255,0.25)' : undefined,
                  color: tabValue === value ? 'white' : undefined,
                }}
              />
            )}
          </ListItemButton>
        ))}
      </List>

      <Divider sx={{ my: 1.5 }} />

      {/* Keresés */}
      <Box sx={{ mb: 1.5 }}>
        <TextField
          placeholder="Keresés..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button size="small" variant="contained" onClick={handleSearch} disabled={loading} fullWidth>
            Keresés
          </Button>
          {hasActiveFilter && (
            <Button size="small" variant="outlined" onClick={handleClearFilters} disabled={loading}>
              Törlés
            </Button>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Típus szűrők */}
      <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Típus
      </Typography>
      <List dense disablePadding>
        {typeGroups.map(({ value, label, Icon, color }) => (
          <ListItemButton
            key={value}
            selected={selectedGroup === value}
            onClick={() => handleGroupSelect(value)}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: `${color}.main` === 'default.main' ? 'action.selected' : `${color}.main`,
                color: color === 'default' ? 'text.primary' : 'white',
                '& .MuiSvgIcon-root': { color: color === 'default' ? 'text.primary' : 'white' },
                '&:hover': {
                  bgcolor: color === 'default' ? 'action.hover' : `${color}.dark`,
                },
              },
            }}
          >
            <Icon sx={{ mr: 1.5, fontSize: 18, color: selectedGroup === value ? 'inherit' : `${color}.main` }} />
            <ListItemText
              primary={label}
              primaryTypographyProps={{ variant: 'body2', fontWeight: selectedGroup === value ? 600 : 400 }}
            />
          </ListItemButton>
        ))}
      </List>
    </>
  );

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: 'calc(100vh - 64px)', py: 3 }}>
      <Container maxWidth="lg">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

          {/* ── Desktop sidebar ── */}
          <Paper
            sx={{
              width: 260, flexShrink: 0,
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column', p: 2,
              position: 'sticky', top: 80,
              maxHeight: 'calc(100vh - 100px)', overflowY: 'auto',
              bgcolor: 'background.paper',
            }}
          >
            {sidebarContent}
          </Paper>

          {/* ── Fő tartalom ── */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>

            {/* Mobil fejléc */}
            <Box sx={{ display: { md: 'none' }, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                    Értesítések
                  </Typography>
                  {unreadCount > 0 && (
                    <Chip label={unreadCount} color="error" size="small" sx={{ height: 20 }} />
                  )}
                </Box>
                <Button
                  size="small"
                  variant={mobileFiltersOpen ? 'contained' : 'outlined'}
                  startIcon={<Tune />}
                  onClick={() => setMobileFiltersOpen(p => !p)}
                >
                  Szűrők
                </Button>
              </Box>
              <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth" sx={{ bgcolor: 'background.paper', borderRadius: 2, minHeight: 36 }}>
                <Tab label="Összes" sx={{ minHeight: 36, py: 0.5 }} />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Olvasatlan
                      {unreadCount > 0 && (
                        <Chip label={unreadCount} color="error" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  }
                  sx={{ minHeight: 36, py: 0.5 }}
                />
                <Tab label="Olvasott" sx={{ minHeight: 36, py: 0.5 }} />
              </Tabs>
            </Box>

            {/* Mobil szűrő collapse */}
            <Collapse in={mobileFiltersOpen} sx={{ display: { md: 'none' } }}>
              <Paper sx={{ p: 2, mb: 1.5, bgcolor: 'background.paper' }}>
                <TextField
                  placeholder="Keresés..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  size="small" fullWidth sx={{ mb: 1 }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18 }} /></InputAdornment> }}
                />
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                  <Button size="small" variant="contained" onClick={handleSearch} disabled={loading}>Keresés</Button>
                  {hasActiveFilter && <Button size="small" variant="outlined" onClick={handleClearFilters} disabled={loading}>Törlés</Button>}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>TÍPUS</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {typeGroups.map(({ value, label, color }) => (
                    <Chip
                      key={value}
                      label={label}
                      size="small"
                      color={selectedGroup === value ? color : 'default'}
                      variant={selectedGroup === value ? 'filled' : 'outlined'}
                      onClick={() => handleGroupSelect(value)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Paper>
            </Collapse>

            {/* Aktív szűrő jelző */}
            {hasActiveFilter && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                {appliedSearch && (
                  <Chip label={`"${appliedSearch}"`} size="small" onDelete={() => { setAppliedSearch(''); setSearchTerm(''); setPage(1); }} />
                )}
                {selectedGroup && (
                  <Chip
                    label={typeGroups.find(g => g.value === selectedGroup)?.label}
                    size="small"
                    color={typeGroups.find(g => g.value === selectedGroup)?.color || 'default'}
                    onDelete={() => { setSelectedGroup(''); setPage(1); }}
                  />
                )}
              </Box>
            )}

            {/* Üres állapot */}
            {notifications.length === 0 && !loading ? (
              <Paper sx={{ bgcolor: '#fff' }}>
                <Box textAlign="center" py={8}>
                  <NotificationsOff sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {hasActiveFilter
                      ? 'Nincs találat a szűrőknek megfelelően'
                      : tabValue === 1
                        ? 'Nincsenek olvasatlan értesítések'
                        : tabValue === 2
                          ? 'Nincsenek olvasott értesítések'
                          : 'Még nincsenek értesítések'
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {hasActiveFilter ? 'Próbálja meg módosítani a szűrőket' : 'Az értesítések itt fognak megjelenni'}
                  </Typography>
                  {hasActiveFilter && (
                    <Button variant="outlined" onClick={handleClearFilters}>Szűrők törlése</Button>
                  )}
                </Box>
              </Paper>
            ) : (
              <Paper sx={{ bgcolor: '#fff' }}>
                {/* Lista fejléc: kijelölés + tömeges műveletek */}
                {notifications.length > 0 && (
                  <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedNotifications.size === notifications.length && notifications.length > 0}
                          indeterminate={selectedNotifications.size > 0 && selectedNotifications.size < notifications.length}
                          onChange={handleSelectAll}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          Összes kijelölése ({notifications.length})
                        </Typography>
                      }
                      sx={{ mr: 0 }}
                    />
                    {selectedNotifications.size > 0 && (
                      <>
                        <Chip
                          label={`${selectedNotifications.size} kijelölve`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 24 }}
                        />
                        <IconButton size="small" title="Olvasottnak jelölés"
                          onClick={() => { setBulkAction('markRead'); setBulkActionDialogOpen(true); }}>
                          <MarkEmailRead sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton size="small" title="Olvasatlannak jelölés"
                          onClick={() => { setBulkAction('markUnread'); setBulkActionDialogOpen(true); }}>
                          <MarkEmailUnread sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton size="small" color="error" title="Törlés"
                          onClick={() => { setBulkAction('delete'); setBulkActionDialogOpen(true); }}>
                          <DeleteSweep sx={{ fontSize: 18 }} />
                        </IconButton>
                      </>
                    )}
                  </Box>
                )}

                <List sx={{ p: 0 }}>
                  {notifications.map((notification, index) => (
                    <React.Fragment key={notification.id}>
                      <ListItem
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                          px: 2, py: 1.5,
                          bgcolor: notification.isRead ? 'transparent' : 'rgba(42,93,50,0.04)',
                          '&:hover': { bgcolor: 'rgba(42,93,50,0.08)' },
                          cursor: getNotificationUrl(notification) ? 'pointer' : 'default',
                          alignItems: 'flex-start',
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedNotifications.has(notification.id)}
                              onChange={(e) => { e.stopPropagation(); handleSelectNotification(notification.id); }}
                              size="small"
                            />
                          }
                          label=""
                          sx={{ mr: 0.5, mt: 0.5, alignSelf: 'flex-start' }}
                        />

                        <ListItemAvatar sx={{ mt: 0.5 }}>
                          <Avatar
                            sx={{
                              bgcolor: `${getNotificationColor(notification.type)}.main`,
                              color: 'white',
                              width: 40, height: 40,
                            }}
                          >
                            {getNotificationIcon(notification.type)}
                          </Avatar>
                        </ListItemAvatar>

                        <Box sx={{ flexGrow: 1, minWidth: 0, mr: 1 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: notification.isRead ? 500 : 700,
                              color: getNotificationUrl(notification) ? 'primary.main' : 'inherit',
                              mb: 0.25,
                            }}
                          >
                            {notification.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: notification.isRead ? 400 : 500, mb: 0.5 }}
                          >
                            {notification.message}
                          </Typography>
                          <Chip
                            label={notificationTypeLabels[notification.type] || notification.type}
                            size="small"
                            color={getNotificationColor(notification.type)}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </Box>

                        <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, ml: 'auto' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
                            {formatDate(notification.createdAt)}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.25 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); notification.isRead ? handleMarkAsUnread(notification.id) : handleMarkAsRead(notification.id); }}
                              title={notification.isRead ? 'Olvasatlannak jelölés' : 'Olvasottnak jelölés'}
                              sx={{ color: 'text.secondary' }}
                            >
                              {notification.isRead
                                ? <MarkEmailUnread sx={{ fontSize: 18 }} />
                                : <MarkEmailRead sx={{ fontSize: 18 }} />}
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => { e.stopPropagation(); handleDeleteNotification(notification.id); }}
                              title="Törlés"
                            >
                              <Delete sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      </ListItem>
                      {index < notifications.length - 1 && <Divider sx={{ ml: 9 }} />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}

            {/* Lapozás */}
            {pagination.pages > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination count={pagination.pages} page={page} onChange={handlePageChange} color="primary" size="small" />
              </Box>
            )}
          </Box>
        </Box>
      </Container>

      {/* Tömeges művelet megerősítés */}
      <Dialog open={bulkActionDialogOpen} onClose={() => setBulkActionDialogOpen(false)}>
        <DialogTitle>Tömeges művelet megerősítése</DialogTitle>
        <DialogContent>
          <Typography>
            {bulkAction === 'markRead' && `${selectedNotifications.size} értesítést jelöl olvasottnak?`}
            {bulkAction === 'markUnread' && `${selectedNotifications.size} értesítést jelöl olvasatlannak?`}
            {bulkAction === 'delete' && `Biztosan törli a kijelölt ${selectedNotifications.size} értesítést?`}
          </Typography>
          {bulkAction === 'delete' && (
            <Alert severity="warning" sx={{ mt: 2 }}>Ez a művelet nem vonható vissza!</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionDialogOpen(false)}>Mégse</Button>
          <Button
            variant="contained"
            color={bulkAction === 'delete' ? 'error' : 'primary'}
            onClick={handleBulkAction}
          >
            {bulkAction === 'markRead' && 'Olvasottnak jelölés'}
            {bulkAction === 'markUnread' && 'Olvasatlannak jelölés'}
            {bulkAction === 'delete' && 'Törlés'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading overlay */}
      {loading && notifications.length > 0 && (
        <Box
          display="flex" justifyContent="center" alignItems="center"
          sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 1000 }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default NotificationsPage;
